import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { catchError, tap, finalize, shareReplay } from 'rxjs/operators';
import { BaseHttpService } from './base-http.service';
import { CONTROLLER_CONSTANTS } from '../constants/controller.constants';
import { ENDPOINT_CONSTANTS } from '../constants/endpoint.constants';
import { ROUTE_CONSTANTS } from '../constants/route.constants';
import { HTTP_STATUS_CODES } from '../constants/error.constants';
import { UserInfo, TokenData, RefreshTokenRequest, RefreshTokenResponse } from '../interfaces/auth.interface';
import { Router } from '@angular/router';
import { ModalService } from '../../features/chat/services/modal.service';
import { NotificationService } from './notification.service';
import { AUTH_MESSAGES } from '../constants/message.constants';
import { TenantService } from './tenant.service';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';
  private readonly ACCESS_TOKEN_EXPIRY_KEY = 'access_token_expiry';
  private readonly REFRESH_TOKEN_EXPIRY_KEY = 'refresh_token_expiry';
  private readonly USER_INFO_KEY = 'user_info';
  private readonly REFRESH_THRESHOLD_SECONDS = 60;

  private userInfoSubject = new BehaviorSubject<UserInfo | null>(null);
  private refreshTokenSubject = new BehaviorSubject<boolean>(false);
  private refreshInFlight$: Observable<RefreshTokenResponse> | null = null;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private heartbeatTimer?: ReturnType<typeof setInterval>;

  public userInfo$ = this.userInfoSubject.asObservable();
  public isRefreshing$ = this.refreshTokenSubject.asObservable();

  constructor(private baseHttp: BaseHttpService, private router: Router, private modalService: ModalService,
    private notificationService: NotificationService, private tenantService: TenantService) {
    this.initializeUserInfo();
    this.startTokenRefreshTimer();
    this.startRefreshHeartbeat();
    this.bindKeepAliveListeners();
  }

  refreshAccessToken(): Observable<RefreshTokenResponse> {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      if (!this.getToken() || this.isTokenExpired()) {
        this.logoutSync();
      }
      return throwError(() => new Error('No refresh token available'));
    }

    const endpoint = `/${CONTROLLER_CONSTANTS.ACCOUNT}/${ENDPOINT_CONSTANTS.REFRESH_TOKEN}`;
    const request: RefreshTokenRequest = { refreshToken };

    this.refreshTokenSubject.next(true);
    this.refreshInFlight$ = this.baseHttp.post<RefreshTokenResponse>(endpoint, request, true).pipe(
      tap((response) => {
        const tokenData = this.normalizeTokenResponse(response);
        if (!tokenData.accessToken) {
          throw new Error('Refresh response did not include an access token');
        }
        tokenData.refreshToken = tokenData.refreshToken || this.getRefreshToken() || '';
        this.setTokens(tokenData);
      }),
      catchError((error) => {
        if (this.isDefinitiveAuthFailure(error)) {
          this.logoutSync();
          return throwError(() => error);
        }

        if (this.getToken() && !this.isTokenExpired()) {
          return of(this.tokensFromStorage());
        }

        return throwError(() => error);
      }),
      finalize(() => {
        this.refreshInFlight$ = null;
        this.refreshTokenSubject.next(false);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.refreshInFlight$;
  }

  ensureFreshAccessToken(): void {
    this.keepSessionAlive();
  }

  setTokens(tokenData: TokenData): void {
    const accessToken = tokenData.accessToken;
    const refreshToken = tokenData.refreshToken || this.getRefreshToken() || '';
    if (!accessToken) {
      return;
    }

    localStorage.setItem(this.TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }

    const accessExpiry = tokenData.accessTokenExpiry || this.getJwtExpiryIso(accessToken) || '';
    const refreshExpiry = tokenData.refreshTokenExpiry
      || this.getJwtExpiryIso(refreshToken)
      || this.getRefreshTokenExpiry()
      || '';

    localStorage.setItem(this.ACCESS_TOKEN_EXPIRY_KEY, String(accessExpiry));
    if (refreshExpiry) {
      localStorage.setItem(this.REFRESH_TOKEN_EXPIRY_KEY, String(refreshExpiry));
    }

    const userInfo = this.extractUserInfoFromToken(accessToken);
    if (userInfo) {
      this.setUserInfo(userInfo);
    }
    if (Array.isArray(tokenData.tenants)) {
      this.tenantService.setTenants(tokenData.tenants);
    }

    this.startTokenRefreshTimer();
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getAccessTokenExpiry(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_EXPIRY_KEY);
  }

  getRefreshTokenExpiry(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_EXPIRY_KEY);
  }

  isUserLoggedIn(): boolean {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    const expiry = this.getRefreshExpiryDate();
    if (!expiry) {
      return true;
    }

    return expiry.getTime() > Date.now();
  }

  isTokenExpired(): boolean {
    const expiry = this.getAccessExpiryDate();
    if (!expiry) {
      return true;
    }

    return expiry.getTime() <= Date.now();
  }

  isTokenExpiringSoon(): boolean {
    const expiry = this.getAccessExpiryDate();
    if (!expiry) {
      return true;
    }

    return expiry.getTime() <= Date.now() + (this.REFRESH_THRESHOLD_SECONDS * 1000);
  }

  isRefreshTokenExpired(): boolean {
    const expiry = this.getRefreshExpiryDate();
    if (!expiry) {
      return false;
    }

    return expiry.getTime() <= Date.now();
  }

  setUserInfo(userInfo: UserInfo): void {
    localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userInfo));
    this.userInfoSubject.next(userInfo);
  }

  getUserInfo(): UserInfo | null {
    const userInfoStr = localStorage.getItem(this.USER_INFO_KEY);
    if (!userInfoStr) {
      return null;
    }

    try {
      return JSON.parse(userInfoStr);
    } catch (error) {
      return null;
    }
  }

  private extractUserInfoFromToken(token: string): UserInfo | null {
    try {
      const payload = this.decodeJwtToken(token);

      return {
        userId: payload.nameid || payload.sub || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'] || '',
        email: payload.email || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || '',
        username: payload.unique_name || payload.username || payload.name || payload.email || '',
        role: payload.role || 'Standard',
        appRole: payload.role || 'Standard',
        firstName: payload.given_name || payload.firstName || '',
        lastName: payload.family_name || payload.lastName || '',
        permissions: payload.permissions || []
      };
    } catch (error) {
      return null;
    }
  }

  private decodeJwtToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      throw error;
    }
  }

  getCurrentUserInfo(): UserInfo | null {
    return this.userInfoSubject.value;
  }

  getUserRole(): string | null {
    const userInfo = this.getUserInfo();
    return userInfo?.role || null;
  }

  /**
   * Persist organogram identity used by chat stream RBAC.
   * Keeps appRole (SME/Standard) intact for UI gates.
   * An unassigned organogram role is stored as null so chat does not
   * send the app role and Brain leaves retrieval open.
   */
  applyOrganogramIdentity(roleName: string | null | undefined, permissions: string[] | null | undefined): void {
    const userInfo = this.getUserInfo();
    if (!userInfo) {
      return;
    }

    if (!userInfo.appRole) {
      const current = (userInfo.role || '').trim();
      userInfo.appRole = current === 'SME' || current === 'Standard' ? current : 'Standard';
    }

    const cleanedPermissions = Array.isArray(permissions)
      ? permissions.filter(p => typeof p === 'string' && p.trim()).map(p => p.trim())
      : [];

    const trimmedRole = (roleName || '').trim();
    userInfo.role = trimmedRole || null;
    userInfo.permissions = cleanedPermissions;
    this.setUserInfo(userInfo);
  }

  /**
   * Organogram title for chat RBAC. App roles are not business roles.
   * Null means unassigned: the caller should omit role and permissions.
   */
  getOrganogramRoleForChat(): string | null {
    const role = (this.getUserInfo()?.role || '').trim();
    if (!role || role === 'Standard' || role === 'SME') {
      return null;
    }
    return role;
  }

  getOrganogramPermissionsForChat(): string[] {
    if (!this.getOrganogramRoleForChat()) {
      return [];
    }
    const raw = this.getUserInfo()?.permissions;
    if (!Array.isArray(raw)) {
      return [];
    }
    return raw.filter(p => typeof p === 'string' && p.trim()).map(p => p.trim());
  }

  getUserEmail(): string | null {
    const userInfo = this.getUserInfo();
    return userInfo?.email || null;
  }

  getUserId(): string | null {
    const userInfo = this.getUserInfo();
    return userInfo?.userId || null;
  }

  hasPermission(permission: string): boolean {
    const userInfo = this.getUserInfo();
    return userInfo?.permissions?.includes(permission) || false;
  }

  hasRole(role: string): boolean {
    const userInfo = this.getUserInfo();
    if (!userInfo) {
      return false;
    }
    return userInfo.appRole === role || userInfo.role === role;
  }

  clearTokens(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.ACCESS_TOKEN_EXPIRY_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_EXPIRY_KEY);
  }

  clearUserInfo(): void {
    localStorage.removeItem(this.USER_INFO_KEY);
    this.userInfoSubject.next(null);
  }

  logout(): Observable<any> {
    const endpoint = `/${CONTROLLER_CONSTANTS.ACCOUNT}/${ENDPOINT_CONSTANTS.LOGOUT}`;
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      this.logoutSync();
      return throwError(() => new Error('No refresh token available'));
    }

    const request: RefreshTokenRequest = { refreshToken };

    return this.baseHttp.post(endpoint, request, true).pipe(
      tap(() => {
        this.logoutSync();
      }),
      catchError((error) => {
        this.logoutSync();
        return throwError(() => error);
      })
    );
  }

  logoutSync(): void {
    this.modalService.dismissAllModals();
    this.clearTokens();
    this.clearUserInfo();
    this.tenantService.clearTenants();
    this.stopTokenRefreshTimer();
    this.refreshInFlight$ = null;
    this.refreshTokenSubject.next(false);
    // this.notificationService.showSuccess(AUTH_MESSAGES.LOGOUT_SUCCESS);
    this.router.navigate([ROUTE_CONSTANTS.LOGIN]);
  }

  private initializeUserInfo(): void {
    const userInfo = this.getUserInfo();
    if (userInfo) {
      this.userInfoSubject.next(userInfo);
    } else {
      const token = this.getToken();
      if (token) {
        const extractedUserInfo = this.extractUserInfoFromToken(token);
        if (extractedUserInfo) {
          this.setUserInfo(extractedUserInfo);
        }
      }
    }
  }

  private startTokenRefreshTimer(): void {
    this.stopTokenRefreshTimer();

    if (!this.getRefreshToken()) {
      return;
    }

    const expiryDate = this.getAccessExpiryDate();
    if (!expiryDate) {
      this.performTokenRefresh();
      return;
    }

    const timeUntilRefresh = expiryDate.getTime() - Date.now() - (this.REFRESH_THRESHOLD_SECONDS * 1000);

    if (timeUntilRefresh > 0) {
      this.refreshTimer = setTimeout(() => this.performTokenRefresh(), timeUntilRefresh);
    } else {
      this.performTokenRefresh();
    }
  }

  private stopTokenRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = undefined;
    }
  }

  private startRefreshHeartbeat(): void {
    this.stopRefreshHeartbeat();
    this.heartbeatTimer = setInterval(() => this.keepSessionAlive(), 30_000);
  }

  private stopRefreshHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  private bindKeepAliveListeners(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.keepSessionAlive();
      }
    });
    window.addEventListener('storage', (event) => {
      if (event.key === this.TOKEN_KEY || event.key === this.REFRESH_TOKEN_KEY) {
        this.startTokenRefreshTimer();
      }
    });
  }

  private keepSessionAlive(): void {
    if (!this.getRefreshToken()) {
      return;
    }
    if (this.isTokenExpired() || this.isTokenExpiringSoon()) {
      this.performTokenRefresh();
    }
  }

  private performTokenRefresh(): void {
    if (!this.getRefreshToken()) {
      return;
    }

    this.refreshAccessToken().subscribe({
      next: () => { },
      error: () => { }
    });
  }

  private getAccessExpiryDate(): Date | null {
    return this.getJwtExpiryDate(this.getToken()) || this.parseDate(this.getAccessTokenExpiry());
  }

  private getRefreshExpiryDate(): Date | null {
    return this.getJwtExpiryDate(this.getRefreshToken()) || this.parseDate(this.getRefreshTokenExpiry());
  }

  private getJwtExpiryDate(token: string | null): Date | null {
    if (!token) {
      return null;
    }

    try {
      const payload = this.decodeJwtToken(token);
      if (!payload?.exp) {
        return null;
      }
      return new Date(payload.exp * 1000);
    } catch {
      return null;
    }
  }

  private getJwtExpiryIso(token: string | null): string | null {
    const expiry = this.getJwtExpiryDate(token);
    return expiry ? expiry.toISOString() : null;
  }

  private parseDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private isDefinitiveAuthFailure(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return error.status === HTTP_STATUS_CODES.BAD_REQUEST
      || error.status === HTTP_STATUS_CODES.UNAUTHORIZED;
  }

  private normalizeTokenResponse(response: any): TokenData {
    const tokenData = response?.data || response;
    return {
      accessToken: tokenData.accessToken ?? tokenData.AccessToken ?? '',
      refreshToken: tokenData.refreshToken ?? tokenData.RefreshToken ?? '',
      accessTokenExpiry: tokenData.accessTokenExpiry ?? tokenData.AccessTokenExpiry ?? '',
      refreshTokenExpiry: tokenData.refreshTokenExpiry ?? tokenData.RefreshTokenExpiry ?? '',
      tenants: tokenData.tenants ?? tokenData.Tenants
    };
  }

  private tokensFromStorage(): RefreshTokenResponse {
    return {
      accessToken: this.getToken() || '',
      refreshToken: this.getRefreshToken() || '',
      accessTokenExpiry: this.getAccessTokenExpiry() || '',
      refreshTokenExpiry: this.getRefreshTokenExpiry() || ''
    };
  }

  ngOnDestroy(): void {
    this.stopTokenRefreshTimer();
    this.stopRefreshHeartbeat();
  }
}
